// Pure planner helpers (no React, no fetch) so the day arithmetic and the
// "good until" rule can be unit-tested in isolation, like dedupe.js.
//
// The plan is an ordered sequence of meals, each with a `thickness` of 1 or
// 2 days (a 2-day meal is cooked once and eaten twice - 2 is the hard cap,
// enforced server-side too). Days are counted from `plan.startDate`:
// day 0 = the start date. Each meal's days are the next `thickness` days
// after the previous meal's days (Fajitas at thickness 2 on Mon 19 -> Mon 19
// + Tue 20).
//
// "Good until" (ROADMAP part 3, v1 rule): the latest target day across the
// meals that use an item - i.e. the LAST day of the LAST meal (by plan order)
// that uses it. Over-strict is safe: it errs towards "needs to be fresher".
// Extras (meal id "0") is never planned, so extras-only items get no hint.

const DAY_MS = 24 * 60 * 60 * 1000

// Parse "YYYY-MM-DD" into a Date at local midnight. (new Date("YYYY-MM-DD")
// parses as UTC, which can shift the day in negative-offset time zones - we
// build the local date from its parts instead.)
function parseDate(dateStr){
  const [year, month, day] = dateStr.split("-").map(Number)
  return new Date(year, month - 1, day)
}

// "YYYY-MM-DD" -> "YYYY-MM-DD" shifted by `days` (may be negative).
export function shiftDate(dateStr, days){
  const d = parseDate(dateStr)
  d.setDate(d.getDate() + days)
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${d.getFullYear()}-${month}-${day}`
}

const WEEKDAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"]

// "YYYY-MM-DD" -> "Mon 19" (weekday + day of month, no year).
export function dayLabel(dateStr){
  const d = parseDate(dateStr)
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()}`
}

// The week's shop cost, e.g. 87.5 -> "£87.50" (always two decimals).
// null/undefined -> null (the cost has not been logged yet).
export function formatCost(cost){
  if(cost === null || cost === undefined) return null
  return `£${Number(cost).toFixed(2)}`
}

// Expand the plan into one entry per planned meal:
//   { id, label, thickness, firstDay, lastDay, firstLabel, lastLabel }
// sorted by position (the plan order). An empty/absent plan -> [].
export function planDays(plan){
  if(!plan || !Array.isArray(plan.meals) || plan.meals.length === 0){
    return []
  }
  const ordered = [...plan.meals].sort((a, b) => a.position - b.position)
  let cursor = 0
  const days = []
  for(const meal of ordered){
    const thickness = meal.thickness === 2 ? 2 : 1
    const firstDay = shiftDate(plan.startDate, cursor)
    const lastDay = shiftDate(plan.startDate, cursor + thickness - 1)
    days.push({
      id: meal.id,
      label: meal.label,
      thickness: thickness,
      firstDay: firstDay,
      lastDay: lastDay,
      firstLabel: dayLabel(firstDay),
      lastLabel: dayLabel(lastDay)
    })
    cursor += thickness
  }
  return days
}

// "Good until" day for one shopping-list item, or null when none of its
// source meals is in the plan. `fromMeal` is the item's list of sources
// ({id, label, ...} - extras has id "0").
//
// v1 rule: the latest target day across the meals that use the item. Plan
// day spans never overlap (each meal's days come after the previous meal's),
// so the latest day is simply the LAST day of the LAST planned meal (by plan
// order) that uses the item. Over-strict is safe: it errs towards "needs to
// be fresher".
export function goodUntil(plan, fromMeal){
  const days = planDays(plan)
  if(days.length === 0 || !Array.isArray(fromMeal)){
    return null
  }
  let latest = null
  for(const source of fromMeal){
    const entry = days.find((day) => day.id === source.id)
    if(entry){
      latest = entry
    }
  }
  return latest ? latest.lastDay : null
}